import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import {
  IconChalkboard,
  IconClipboard,
  IconFolders,
  IconLayoutDashboard,
  IconMenu2,
  IconSchool,
  IconSmartHome,
  IconTicket,
  IconUsers,
  IconUsersGroup,
  IconUserStar,
  IconWallet,
  IconReportMoney,
  IconMessageUser,
} from "@tabler/icons-react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  useTheme,
} from "@mui/material";

import Logo from "../assets/svg/logo-full.svg";
import LogoMiddle from "../assets/svg/logo-middle.svg";

import ListItemLink from "./ListItemButton";
import UserConfigMenu from "./UserConfigMenu";
import { listStoreData } from "../services/dashboard";
import { useQuery } from "@tanstack/react-query";
import { initializeSocket } from "../services/socket";
import { useUserStore } from "../stores/user";
import ConfirmCheckIn from "./GympassCheckIn";
import PageModal from "./FinanceModal";
import AnalyticsTracker from "./AnalyticsTracker";
import BranchSelect from "./BranchSelect";
import { useMediaQuery } from "@mui/material";
import StudentDrawerHost from "./StudentDrawerHost";

const Menu = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useUserStore();

  const [middleMenu, setMiddleMenu] = useState(false);
  const [openSubmenuKey, setOpenKey] = useState<string>("");
  const [checkInQueue, setCheckInQueue] = useState<any[]>([]);
  const [openCheckIn, setOpenCheckIn] = useState(false);

  const setOpenSubmenuKey = (e: any) => {
    setOpenKey(e);
    if (middleMenu && Boolean(e)) setMiddleMenu(false);
  };

  const { data, refetch }: { data: any; refetch: () => void } = useQuery({
    queryKey: ["listStoreDataPeriod"],
    queryFn: async () => await listStoreData(),
    initialData: {},
    enabled: false,
  });

  useEffect(() => {
    if (data?.needSelectPlan) {
      navigate("/free-period-expired");
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    const socket = initializeSocket(String(user?.storeId));

    socket.on("check_in", (msg) => {
      setCheckInQueue((prev) => [...prev, msg]);
      setOpenCheckIn(true);
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.storeId]);

  const handleCloseCheckIn = () => {
    setCheckInQueue((prev) => {
      const updated = prev.slice(1);
      if (updated.length === 0) setOpenCheckIn(false);
      return updated;
    });
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <>
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: theme.zIndex.drawer + 1,
            width: "100%",
            backgroundColor: "secondary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 16px",
            boxShadow: theme.shadows[4],
          }}
        >
          <img src={LogoMiddle} alt="logo-mobile" style={{ height: 32 }} />
          <IconButton onClick={() => setDrawerOpen(true)}>
            <IconMenu2 color="#fff" />
          </IconButton>
        </Box>
      )}

      <Drawer
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        anchor={isMobile ? "right" : "left"}
        variant={isMobile ? "temporary" : "permanent"}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: middleMenu ? theme.spacing(10) : theme.spacing(28),
            padding: 1,
            backgroundColor: "secondary.main",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          overflow="auto"
        >
          <Box
            onClick={() => setMiddleMenu(!middleMenu)}
            sx={{
              width: "100%",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {middleMenu ? (
              <img src={LogoMiddle} alt="logoMiddle-menu" />
            ) : (
              <img src={Logo} alt="logo-menu" />
            )}
            <Divider sx={{ marginTop: 2 }} />
          </Box>

          <Box flex={1}>
            <List component="nav">
              <ListItemLink
                to="/dashboard"
                label="Início"
                icon={<IconLayoutDashboard />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/students"
                label="Alunos"
                icon={<IconSchool />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/visitantes"
                label="Visitantes"
                icon={<IconMessageUser />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/responsaveis"
                label="Responsáveis"
                icon={<IconUsers />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/professors"
                label="Professores"
                icon={<IconUsersGroup />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/admins"
                label="Administradores"
                icon={<IconUserStar />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/classes"
                label="Aulas"
                icon={<IconChalkboard />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                label="Turmas"
                to="turmas"
                icon={<IconClipboard />}
                middleMenu={middleMenu}
                openKey={openSubmenuKey}
                setOpenKey={setOpenSubmenuKey}
                submenu={[
                  { to: "/class", label: "Turmas" },
                  { to: "/spaces", label: "Espaços" },
                  { to: "/modality", label: "Modalidades" },
                ]}
              />
              <ListItemLink
                label="Planos"
                to="plans"
                icon={<IconFolders />}
                middleMenu={middleMenu}
                openKey={openSubmenuKey}
                setOpenKey={setOpenSubmenuKey}
                submenu={[
                  { to: "/plans", label: "Planos" },
                  { to: "/coupons", label: "Cupons" },
                ]}
              />
              <ListItemLink
                label="Marketplace"
                to="/marketplace"
                icon={<IconSmartHome />}
                middleMenu={middleMenu}
                openKey={openSubmenuKey}
                setOpenKey={setOpenSubmenuKey}
                submenu={[
                  { to: "/products", label: "Produtos" },
                  { to: "/orders", label: "Pedidos" },
                ]}
              />
              <ListItemLink
                to="/gymapss"
                label="Gympass"
                icon={<IconTicket />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="payable-accounts"
                label="Contas a Pagar"
                icon={<IconReportMoney />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
              <ListItemLink
                to="/financeiro"
                label="Financeiro"
                icon={<IconWallet />}
                middleMenu={middleMenu}
                setOpenKey={setOpenSubmenuKey}
              />
            </List>
          </Box>
          <Box sx={{ padding: 1 }}>
            <BranchSelect middleMenu={middleMenu} />
          </Box>
          <Box sx={{ padding: 1 }}>
            <UserConfigMenu middleMenu={middleMenu} />
          </Box>
        </Box>
      </Drawer>
      <Box
        minHeight="100vh"
        marginLeft={
          !isMobile ? (middleMenu ? theme.spacing(10) : theme.spacing(28)) : 0
        }
        sx={{
          backgroundColor: "secondary.light",
          ...(isMobile && { paddingTop: "56px" }),
        }}
      >
        <Outlet />
      </Box>

      {checkInQueue.length > 0 && (
        <ConfirmCheckIn
          open={openCheckIn}
          data={checkInQueue[0]}
          onClose={handleCloseCheckIn}
        />
      )}
      <PageModal />
      <AnalyticsTracker />
      <StudentDrawerHost />
    </>
  );
};

export default Menu;
